/**
 * Connected accounts (§7.2, §11.2): connect, health, re-auth.
 *
 * Re-authorization is mobile-critical (§7.8) — it is the thing an owner has to do
 * from wherever they are when a rental is failing — so it is a single primary
 * button per account, never a nested settings flow.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import { routes } from '../../api/routes';
import { useBotOutlet } from '../botOutlet';
import type { Page, SocialAccount } from '../../api/types';
import { Gate } from '../../components/Gate';
import { Card, Loading, Badge } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { ConnectAccountForm } from '../../components/ConnectAccountForm';
import { AccountHealthBadge, healthCopy } from '../../components/health';
import { ConfirmButton } from '../../components/ConfirmButton';
import { localDateTime } from '../../lib/format';

export function ConnectedAccounts() {
  const { bot, caps, reload } = useBotOutlet();
  const list = useApi<Page<SocialAccount>>(routes.bots.socialAccounts(bot.id), { pollMs: 60_000 });
  const [showConnect, setShowConnect] = useState(false);

  // TODO(api): there is no per-account health re-check. §10.4 has the bot-wide
  // GET /bots/{id}/health and nothing that re-probes one account on demand, so
  // the list polls instead of offering a button that cannot do anything. It
  // needs POST /api/v1/bots/{id}/social-accounts/{sid}/health-check.
  const reauth = useMutation<{ id: string; secret: string }, SocialAccount>((a) =>
    routes.bots.socialAccountReauthorization(bot.id, a.id),
  );
  const detach = useMutation<{ id: string }, unknown>((a) => routes.bots.socialAccount(bot.id, a.id), {
    method: 'DELETE',
  });
  const [secrets, setSecrets] = useState<Record<string, string>>({});

  if (list.initialLoading) return <Loading label="Loading accounts" />;
  if (list.problem) return <ErrorView problem={list.problem} onRetry={list.reload} />;

  const accounts = list.data?.data ?? [];

  return (
    <div className="accounts">
      {accounts.length === 0 ? (
        <Card>
          <EmptyState
            heading="No account is connected"
            body="A bot needs a connected account before it can post, read or be listed."
          />
        </Card>
      ) : (
        <Card title="Connected accounts">
          <ul className="account-list">
            {accounts.map((a) => {
              const copy = healthCopy(a.status);
              const needsReauth = a.status === 'needs_reauth' || a.status === 'revoked';
              return (
                <li key={a.id} className="account">
                  <div className="account-main">
                    <p>
                      <strong>
                        {a.platform} · @{a.external_handle}
                      </strong>{' '}
                      <AccountHealthBadge status={a.status} />
                      {a.bot_label_applied ? (
                        <Badge tone="ok">labelled as a bot</Badge>
                      ) : (
                        <Badge tone="warn">not labelled as a bot</Badge>
                      )}
                    </p>
                    {copy.action ? <p className="health-action">{copy.action}</p> : null}
                    <p className="muted">
                      Checked {localDateTime(a.health_checked_at)} · last success{' '}
                      {localDateTime(a.last_success_at)}
                    </p>

                    {needsReauth ? (
                      <form
                        className="reauth"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const secret = secrets[a.id] ?? '';
                          if ((await reauth.run({ id: a.id, secret })) !== null) {
                            setSecrets({ ...secrets, [a.id]: '' });
                            list.reload();
                            reload();
                          }
                        }}
                      >
                        <label htmlFor={`reauth-${a.id}`}>New app password or token</label>
                        <input
                          id={`reauth-${a.id}`}
                          className="input"
                          type="password"
                          autoComplete="off"
                          value={secrets[a.id] ?? ''}
                          onChange={(e) => setSecrets({ ...secrets, [a.id]: e.target.value })}
                        />
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={reauth.pending || !(secrets[a.id] ?? '').length}
                        >
                          {reauth.pending ? 'Re-authorizing…' : 'Re-authorize'}
                        </button>
                        {reauth.problem ? <InlineError problem={reauth.problem} /> : null}
                      </form>
                    ) : null}
                  </div>

                  <div className="account-actions">
                    <Gate caps={caps} capability="attach_social_account">
                      {(disabled) => (
                        <ConfirmButton
                          label="Disconnect"
                          variant="ghost"
                          confirmTitle="Disconnect this account?"
                          confirmBody={
                            <p>
                              The bot stops posting to it immediately. Any active rental loses the
                              permissions that depend on it.
                            </p>
                          }
                          confirmLabel="Disconnect"
                          disabled={disabled}
                          onConfirm={async () => {
                            await detach.run({ id: a.id });
                            list.reload();
                            reload();
                          }}
                        />
                      )}
                    </Gate>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <Gate caps={caps} capability="attach_social_account">
        {(disabled) =>
          showConnect ? (
            <ConnectAccountForm
              botId={bot.id}
              onConnected={() => {
                setShowConnect(false);
                list.reload();
                reload();
              }}
            />
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={disabled}
              onClick={() => setShowConnect(true)}
            >
              Connect an account
            </button>
          )
        }
      </Gate>
    </div>
  );
}
